import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, TextField, InputAdornment, MenuItem, Select,
  FormControl, InputLabel, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Chip, TablePagination, IconButton, Tooltip,
    CircularProgress, Alert, Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useAuth } from "../../contexts/AuthContext";
import { helpDeskService } from "../../services/helpDeskService";

const STATUS_COLORS = {
  Open:          { bg: "#fee2e2", fg: "#991b1b" },
  "In Progress": { bg: "#dbeafe", fg: "#1e40af" },
  Resolved:      { bg: "#dcfce7", fg: "#166534" },
  Closed:        { bg: "#f3f4f6", fg: "#374151" },
};

const STATUS_OPTIONS = ["Open", "In Progress", "Resolved", "Closed"];

const TechnicianTickets = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // ── Live data state (replaces useDataStore) ──────────────────────────────
  const [tickets, setTickets] = useState([]);
  const [assets, setAssets]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Fetch tickets + assets directly from the database ────────────────────
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);

    try {
      const companyId = currentUser?.companyId;
      if (!companyId) {
        setTickets([]);
        setAssets([]);
        return;
      }

      const myUid   = currentUser?.uid || currentUser?.id || "";
      const myEmail = currentUser?.email || currentUser?.userEmail || "";

      const [fetchedTickets, fetchedAssets] = await Promise.all([
        helpDeskService.getTicketsForTechnician(companyId, myUid, myEmail),
        helpDeskService.getCompanyAssets(companyId),
      ]);

      setTickets(fetchedTickets);
      setAssets(fetchedAssets);
    } catch (err) {
      console.error("[TechnicianTickets] Failed to fetch data:", err);
      setError(err.message || "Failed to load tickets.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentUser?.companyId, currentUser?.uid, currentUser?.id,
      currentUser?.email, currentUser?.userEmail]);

  // Initial load
  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh when tab regains focus (same pattern as AdminHelpDesk)
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") fetchData(true);
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [fetchData]);

  // ── Derived data ─────────────────────────────────────────────────────────
  const assetById = useMemo(() => {
    const m = new Map();
    assets.forEach((a) => m.set(a.id, a));
    return m;
  }, [assets]);

  const enrichedTickets = useMemo(
    () => tickets.map((t) => ({
      ...t,
      _asset: t.assetId ? assetById.get(t.assetId) : null,
    })),
    [tickets, assetById],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enrichedTickets
      .filter((t) => statusFilter === "all" || t.status === statusFilter)
      .filter((t) => {
        if (!q) return true;
        const hay = [
          t.ticketId, t.issueType, t.description, t.userEmail,
          t.assetName, t.assetTag, t._asset?.name, t._asset?.assetTag,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const at = new Date(a.metadata?.updatedAt || 0);
        const bt = new Date(b.metadata?.updatedAt || 0);
        return bt - at;
      });
  }, [enrichedTickets, search, statusFilter]);

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // ── Helpers ──────────────────────────────────────────────────────────────
  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? "—" : dt.toLocaleDateString();
  };

  const warrantyBadge = (asset) => {
    if (!asset?.warrantyExpiry) return <Chip size="small" label="N/A" />;
    const exp = new Date(asset.warrantyExpiry);
    if (isNaN(exp)) return <Chip size="small" label="N/A" />;
    const days = Math.ceil((exp - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0)   return <Chip size="small" label="Expired"   sx={{ bgcolor: "#fee2e2", color: "#991b1b" }} />;
    if (days <= 30) return <Chip size="small" label={`${days}d left`} sx={{ bgcolor: "#fef3c7", color: "#92400e" }} />;
    return <Chip size="small" label="Active" sx={{ bgcolor: "#dcfce7", color: "#166534" }} />;
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>My Tickets</Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
        You are viewing all tickets assigned to you. Click on any ticket to update its status, resolution notes, and costs.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search tickets, customers, assets…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: "1 1 300px" }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3 }}>
        <TableContainer>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                      <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb" }}>
                <TableCell><b>Ticket</b></TableCell>
                <TableCell><b>Raised By</b></TableCell>
                <TableCell><b>Asset</b></TableCell>
                <TableCell><b>Warranty</b></TableCell>
                <TableCell><b>Priority</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Created</b></TableCell>
                <TableCell><b>Resolved</b></TableCell>
                               <TableCell><b>Resolved By</b></TableCell>
                <TableCell><b>Invoice</b></TableCell> 
                <TableCell><b>Cost</b></TableCell>
                <TableCell align="right"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
                       <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                                   <TableCell colSpan={12} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No tickets match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((t) => {
                  const color = STATUS_COLORS[t.status] || STATUS_COLORS.Open;
                  return (
                    <TableRow
                      key={t.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        borderLeft: "3px solid #16a34a", // Always green since they are all assigned to them
                      }}
                      onClick={() => navigate(`/technician/tickets/${t.id}`)}
                    >
                      <TableCell>
                        <Typography fontWeight={600} fontSize="0.85rem">
                          {t.ticketId ? `#${t.ticketId.slice(0, 8)}` : `#${t.id.slice(0, 8)}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block", maxWidth: 220 }}
                        >
                          {t.issueType || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>{t.userEmail || "—"}</TableCell>
                      <TableCell>
                        <Typography fontSize="0.85rem">{t.assetName || "—"}</Typography>
                        {t.assetTag && (
                          <Typography variant="caption" color="text.secondary">
                            {t.assetTag}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{warrantyBadge(t._asset)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={t.priority || "Medium"} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t.status}
                          sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        {fmtDate(t.metadata?.createdAt || t.createdAt)}
                      </TableCell>
                      <TableCell>
                        {fmtDate(t.resolvedAt)}
                      </TableCell>
                                         <TableCell>
                        {t.resolvedBy || "—"}
                      </TableCell>
                      
                      {/* ── NEW INVOICE CELL ── */}
                      <TableCell>
                        {t.maintenanceInvoice?.downloadURL && t.maintenanceInvoice?.fileName ? (
                          <Tooltip title={`View ${t.maintenanceInvoice.fileName}`} arrow>
                            <Button 
                              size="small" 
                              startIcon={<AttachFileIcon sx={{ fontSize: 13 }} />} 
                              onClick={(e) => {
                                e.stopPropagation(); // prevent row click
                                helpDeskService.openInvoiceFile(t.maintenanceInvoice.downloadURL, t.maintenanceInvoice.fileName);
                              }} 
                              sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', p: '2px 8px' }}
                            >
                              {t.maintenanceInvoice.fileName.length > 12 
                                ? t.maintenanceInvoice.fileName.slice(0, 12) + '...' 
                                : t.maintenanceInvoice.fileName}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {t.resolutionCost ? `₹${Number(t.resolutionCost).toLocaleString('en-IN')}` : "—"}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Open">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/technician/tickets/${t.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Card>
    </Box>
  );
};

export default TechnicianTickets;